import type { Messages } from '@/types'

const messages: Messages = {
    common: {
        actions: {
            add: 'Hinzufügen',
            edit: 'Bearbeiten',
            delete: 'Löschen',
            cancel: 'Abbrechen',
            back: 'Zurück',
            save: 'Speichern',
            update: 'Aktualisieren',
            reset: 'Zurücksetzen',
            refresh: 'Aktualisieren',
            backToList: '@:common.back zur Liste',
            resetDefaults: '@:common.reset auf Standardwerte',
            confirm: 'Bestätigen',
            proceed: 'Fortfahren',
            close: 'Schließen',
            apply: 'Anwenden',
            search: 'Suchen',
            filter: 'Filtern',
            clear: 'Löschen',
            select: 'Auswählen',
            upload: 'Hochladen',
            download: 'Herunterladen',
            view: 'Anzeigen',
            sort: 'Sortieren',
            seeMore: 'Mehr anzeigen'
        },
        status: {
            success: 'Erfolg',
            error: 'Fehler',
            loading: 'Wird geladen...'
        },
        messages: {
            deleteConfirm: 'Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?',
            generalError: 'Etwas ist schief gelaufen! Bitte versuchen Sie es erneut',
            addSuccess: 'Datensatz erfolgreich hinzugefügt!',
            updateSuccess: 'Datensatz erfolgreich aktualisiert!',
        },
        placeholder: {
            avatar: '?'
        },
        labels: {
            loading: 'Wird geladen',
            noResults: 'Keine Ergebnisse gefunden',
            noData: 'Keine Daten verfügbar',
            selected: 'Ausgewählt',
            required: 'Erforderlich',
            optional: 'Optional',
            search: 'Suchen...',
            filter: 'Filtern...',
            from: 'Von',
            to: 'Bis',
            date: 'Datum',
            status: 'Status',
            item: 'Element {index}',
            addItem: 'Element Hinzufügen',
            removeItem: 'Element Entfernen',
            skipToMain: 'Zum Hauptinhalt springen',
            openNavigation: 'Navigation öffnen',
            closeNavigation: 'Navigation schließen',
            mainNavigation: 'Hauptnavigation',
            moveUp: 'Nach Oben Verschieben',
            moveDown: 'Nach Unten Verschieben'
        },
        errors: {
            upload: 'Datei konnte nicht hochgeladen werden',
            download: 'Datei konnte nicht heruntergeladen werden',
            generic: 'Ein Fehler ist aufgetreten',
            notFound: 'Nicht gefunden',
            unauthorized: 'Nicht autorisierter Zugriff',
            validation: 'Bitte überprüfen Sie Ihre Eingabe'
        },
        validation: {
            required: 'Dieses Feld ist erforderlich',
            email: 'Ungültige E-Mail-Adresse',
            minLength: 'Mindestlänge ist {length}',
            maxLength: 'Maximale Länge ist {length}',
            min: 'Mindestwert ist {min}',
            max: 'Maximalwert ist {max}',
            matches: 'Ungültiges Format',
            unique: 'Wert muss eindeutig sein',
            confirm: 'Passwörter stimmen nicht überein'
        }
    },
    pages: {
        users: {
            title: 'Benutzer',
            description: 'Systembenutzer verwalten',
            actions: {
                invite: 'Benutzer Einladen',
                resetPassword: 'Passwort Zurücksetzen'
            }
        },
        settings: {
            title: 'Einstellungen',
            actions: {
                resetDefaults: 'Auf Standardwerte Zurücksetzen'
            }
        },
        reports: {
            title: 'Berichte',
            description: 'Berichte erstellen und herunterladen',
            actions: {
                generateReport: 'Bericht Erstellen',
                downloadCsv: 'CSV Herunterladen',
                startDate: 'Startdatum',
                endDate: 'Enddatum',
                apps: 'Apps',
            },
            error: {
                generation: 'Bericht konnte nicht erstellt werden',
                download: 'Bericht konnte nicht heruntergeladen werden'
            }
        }
    },
    navigation: {
        home: 'Startseite',
        library: 'Bibliothek',
        search: 'Suchen',
        login: 'Anmelden',
        signInToGetPremium: 'Premium holen',
        subscribe: 'Abonnieren',
        support: 'Support',
        settings: 'Einstellungen',
        logout: 'Abmelden'
    },
    settings: {
        title: 'Einstellungen',
        description: 'Verwalten Sie Ihre Kontoeinstellungen, Präferenzen und Profilinformationen.',
        sections: 'Einstellungsbereiche',
        menu: {
            profile: 'Profil',
            account: 'Konto',
            general: 'Allgemein',
            purchases: 'Käufe',
            activity: 'Aktivität'
        },
        profile: {
            title: 'Profil',
            changeImage: 'Bild Ändern',
            updateProfile: 'Profil Aktualisieren',
            changePassword: 'Passwort Ändern',
            updatePassword: 'Passwort Aktualisieren',
            fields: {
                fullName: 'Vollständiger Name',
                emailAddress: 'E-Mail-Adresse',
                dateOfBirth: 'Geburtsdatum',
                currentPassword: 'Aktuelles Passwort',
                newPassword: 'Neues Passwort',
                confirmNewPassword: 'Neues Passwort Bestätigen'
            },
            toasts: {
                imageUpdated: 'Bild Aktualisiert',
                imageUpdatedMessage: 'Ihr Profilbild wurde erfolgreich aktualisiert',
                uploadFailed: 'Upload Fehlgeschlagen',
                profileUpdated: 'Profil Aktualisiert',
                profileUpdatedMessage: 'Ihr Profil wurde erfolgreich aktualisiert',
                passwordUpdated: 'Passwort Aktualisiert',
                passwordUpdatedMessage: 'Ihr Passwort wurde erfolgreich aktualisiert',
                passwordError: 'Passwort-Fehler',
                passwordMismatch: 'Die Passwörter stimmen nicht überein',
                passwordTooWeak: 'Das Passwort ist zu schwach'
            }
        },
        account: {
            title: 'Konto',
            accountInformation: 'Kontoinformationen',
            emailAddress: 'E-Mail-Adresse',
            memberSince: 'Mitglied seit',
            redeemPromoCode: 'Aktionscode einlösen',
            redeemPromoCodeDescription: 'Haben Sie einen Aktions- oder Geschenkcode? Geben Sie ihn hier ein, um ihn auf Ihr Konto oder Ihr nächstes Abo anzuwenden.',
            subscription: {
                title: 'Abonnement',
                currentPlan: 'Aktueller Plan',
                premiumSubscription: 'Premium-Abonnement',
                nextRenewal: 'Nächste Verlängerung:',
                accessUntil: 'Zugang bis:',
                manageInAppStore: 'Ihr Abonnement wurde über den App Store erworben. Zum Kündigen oder Ändern öffnen Sie Abonnements in Ihren Apple-ID-Einstellungen auf dem Gerät, mit dem Sie abonniert haben.',
                manageInPlayStore: 'Ihr Abonnement wurde über Google Play erworben. Zum Kündigen oder Ändern öffnen Sie Abonnements im Google Play Store auf dem Gerät, mit dem Sie abonniert haben.',
                notSubscribed: 'Sie sind derzeit nicht abonniert.',
                subscribeNow: 'Jetzt Abonnieren',
                findOutMore: 'Mehr Erfahren',
                cancelSubscription: 'Abonnement Kündigen',
                cancelSubscriptionTitle: 'Abonnement Kündigen',
                cancelSubscriptionMessage: 'Sind Sie sicher, dass Sie Ihr Abonnement kündigen möchten? Sie verlieren den Zugang zu Premium-Inhalten am Ende Ihrer aktuellen Abrechnungsperiode.',
                keepSubscription: 'Abonnement Behalten',
                toasts: {
                    subscriptionActivated: 'Abonnement Aktiviert',
                    subscriptionActivatedMessage: 'Willkommen bei Premium!',
                    subscriptionFailed: 'Abonnement Fehlgeschlagen',
                    paymentMethodUpdated: 'Zahlungsmethode Aktualisiert',
                    paymentMethodUpdatedMessage: 'Ihre Zahlungsinformationen wurden erfolgreich aktualisiert',
                    subscriptionCancelled: 'Abonnement Gekündigt',
                    subscriptionCancelledMessage: 'Ihr Abonnement endet am Ende der aktuellen Abrechnungsperiode',
                    cancellationFailed: 'Kündigung Fehlgeschlagen'
                }
            },
            closeAccount: {
                title: 'Konto Schließen',
                requestDescription: 'Dauerhafte Schließung Ihres Kontos beantragen. Diese Aktion kann nicht rückgängig gemacht werden.',
                requestButton: 'Kontoschließung Beantragen',
                confirmationTitle: 'Sind Sie sicher, dass Sie Ihr Konto deaktivieren möchten? Dies wird:',
                confirmationItems: {
                    cancelSubscription: 'Ihr aktives Abonnement kündigen (falls vorhanden)',
                    removeCommunications: 'Sie von allen Kommunikationen entfernen',
                    scheduleDeletion: 'Ihre Daten nach 30 Tagen dauerhaft löschen'
                },
                confirmationWarning: 'Sie können Ihren Deaktivierungsantrag jederzeit innerhalb von 30 Tagen stornieren. Nach dieser Frist werden Ihr Konto und alle zugehörigen Daten dauerhaft gelöscht.',
                confirmationCheckbox: 'Ich verstehe, dass mein Konto nach 30 Tagen dauerhaft gelöscht wird',
                deactivateAccount: 'Konto Deaktivieren',
                cancel: 'Abbrechen',
                scheduledTitle: 'Kontodeaktivierung Geplant',
                scheduledMessage: 'Ihr Konto ist für die dauerhafte Löschung am geplant',
                scheduledWarning: 'Während dieser Zeit können Sie sich nicht bei Ihrem Konto anmelden. Wenn Sie Ihre Meinung ändern, können Sie den Deaktivierungsprozess abbrechen.',
                cancelDeactivation: 'Kontodeaktivierung Abbrechen',
                toasts: {
                    deactivationRequested: 'Kontodeaktivierung Beantragt',
                    deactivationRequestedMessage: 'Ihr Konto wird in 30 Tagen dauerhaft gelöscht',
                    deactivationFailed: 'Deaktivierung Fehlgeschlagen',
                    deactivationCancelled: 'Deaktivierung Abgebrochen',
                    deactivationCancelledMessage: 'Ihr Konto ist nicht mehr für die Löschung geplant'
                }
            }
        },
        purchases: {
            title: 'Kaufhistorie',
            noPurchases: 'Keine Käufe gefunden',
            invoice: 'Rechnung'
        },
        activity: {
            title: 'Aktivitätszeitachse',
            dateRange: 'Datumsbereich',
            activityType: 'Aktivitätstyp',
            noActivities: 'Keine Aktivitäten gefunden',
            resume: 'Fortsetzen',
            loadMore: 'Mehr Laden',
            filters: {
                last7Days: 'Letzte 7 Tage',
                last30Days: 'Letzte 30 Tage',
                lastYear: 'Letztes Jahr',
                allTime: 'Gesamte Zeit',
                allActivities: 'Alle Aktivitäten',
                listening: 'Hören',
                purchases: 'Käufe',
                likes: 'Gefällt mir',
                bookmarks: 'Lesezeichen'
            },
            types: {
                listenedTo: 'Gehört {title}',
                purchased: 'Gekauft {title}',
                liked: 'Gefällt {title}',
                bookmarked: 'Markiert {title}'
            }
        },
        appSettings: {
            title: 'App-Einstellungen',
            darkMode: 'Dunkler Modus',
            language: 'Sprache',
            languageOptions: {
                english: 'Englisch',
                spanish: 'Spanisch',
                french: 'Französisch',
                german: 'Deutsch',
                italian: 'Italienisch',
                portuguese: 'Portugiesisch',
                dutch: 'Niederländisch',
                slovak: 'Slowakisch',
                russian: 'Russisch',
                arabic: 'العربية'
            }
        },
        notifications: {
            title: 'Benachrichtigungen',
            emailNotifications: 'E-Mail-Benachrichtigungen',
            emailNotificationsHint: 'Updates per E-Mail erhalten',
            pushNotifications: 'Push-Benachrichtigungen',
            pushNotificationsHint: 'Push-Benachrichtigungen erhalten',
            updatePreferences: 'Einstellungen Aktualisieren'
        },
        toasts: {
            themeUpdated: 'Design Aktualisiert',
            themeUpdatedMessage: 'Ihre Design-Präferenz wurde gespeichert',
            languageUpdated: 'Sprache Aktualisiert',
            languageUpdatedMessage: 'Ihre Sprachpräferenz wurde gespeichert',
            preferencesUpdated: 'Einstellungen Aktualisiert',
            preferencesUpdatedMessage: 'Ihre Benachrichtigungseinstellungen wurden gespeichert',
            updateFailed: 'Aktualisierung Fehlgeschlagen',
            updateFailedMessage: 'Fehler beim Aktualisieren der Einstellung',
            updateFailedNotificationMessage: 'Fehler beim Aktualisieren der Benachrichtigungseinstellungen'
        }
    },
    auth: {
        login: {
            title: 'Anmelden',
            description: 'Melden Sie sich bei Ihrem Heard-Konto an, um auf Ihre Bibliothek und Premium-Inhalte zuzugreifen',
            signIn: 'Anmelden',
            signInToAccount: 'Melden Sie sich bei Ihrem Konto an',
            noAccount: 'Haben Sie kein Konto?',
            signUp: 'Registrieren',
            continueAsGuest: 'Als Gast fortfahren',
            or: 'Oder',
            agreeToTerms: 'Durch die Anmeldung stimmen Sie unseren',
            termsOfService: 'Nutzungsbedingungen',
            privacyPolicy: 'Datenschutzrichtlinie',
            forgotPassword: 'Passwort vergessen?'
        },
        register: {
            title: 'Registrieren',
            description: 'Erstellen Sie ein neues Heard-Konto, um auf Ihre Bibliothek und Premium-Inhalte zuzugreifen',
            createAccount: 'Konto erstellen',
            createYourAccount: 'Erstellen Sie Ihr Konto',
            orSignIn: 'Oder',
            signIn: 'anmelden',
            backToHome: 'Zurück zur Startseite',
            agreeToTerms: 'Durch die Registrierung stimmen Sie unseren',
            termsOfService: 'Nutzungsbedingungen',
            privacyPolicy: 'Datenschutzrichtlinie'
        },
        forms: {
            email: 'E-Mail-Adresse',
            password: 'Passwort',
            confirmPassword: 'Passwort bestätigen',
            fullName: 'Vorname Nachname',
            rememberMe: 'Angemeldet bleiben',
            signIn: 'Anmelden',
            createAccount: 'Konto erstellen'
        }
    },
    player: {
        chapters: 'Kapitel',
        options: 'Optionen',
        playbackSpeed: 'Wiedergabegeschwindigkeit',
        share: 'Teilen',
        linkCopied: 'Link Kopiert',
        linkCopiedMessage: 'Freigabelink in die Zwischenablage kopiert!',
        shareError: 'Fehler beim Teilen',
        shareErrorMessage: 'Freigabelink konnte nicht kopiert werden. Bitte versuchen Sie es erneut.',
        bookmarkAdded: 'Erfolg',
        bookmarkAddedMessage: 'Lesezeichen hinzugefügt!',
        jumpedToBookmark: 'Erfolg',
        jumpedToBookmarkMessage: 'Zum Lesezeichen gesprungen!',
        likeError: 'Fehler beim Gefällt mir',
        likeErrorMessage: 'Gefällt-mir-Status konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        bookmarkError: 'Fehler beim Lesezeichen',
        bookmarkErrorMessage: 'Lesezeichen konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        favorites: {
            add: 'Zu Favoriten Hinzufügen',
            remove: 'Aus Favoriten Entfernen'
        },
        unknownChapter: 'Unbekanntes Kapitel',
        unknownTitle: 'Unbekannter Titel',
        unknownAuthor: 'Unbekannter Autor'
    },
    details: {
        starring: 'Hauptdarsteller:',
        author: 'Autor',
        narrator: 'Erzähler',
        pause: 'Pause',
        purchased: 'Gekauft',
        continueReading: 'Weiterlesen',
        continuePlaying: 'Weiter Abspielen',
        play: 'Abspielen',
        loading: 'Laden...',
        noChapters: 'Keine Kapitel verfügbar',
        chapterFreeLabel: 'Kostenlos',
        listen: 'Anhören',
        purchaseSuccess: 'Kauf erfolgreich',
        purchaseSuccessMessage: '{title} wurde zu Ihrer Bibliothek hinzugefügt',
        subscriptionSuccess: 'Abonnement erfolgreich',
        subscriptionSuccessMessage: 'Willkommen bei Heard Premium!',
        subscriptionFailed: 'Abonnement fehlgeschlagen',
        subscriptionErrorMessage: 'Beim Verarbeiten Ihres Abonnements ist ein Fehler aufgetreten.',
        info: 'Info',
        more: 'Mehr',
        episodes: 'Episoden',
        about: 'Über',
        series: 'Serie',
        producedBy: 'Produziert von',
        recommendations: 'Das könnte Ihnen auch gefallen',
        moreFromCategory: 'Mehr aus {name}',
        moreFromAuthor: 'Mehr von {name}',
        moreFromCast: 'Mehr von {name}',
        moreFromSeries: 'Mehr aus {name}',
        noRelated: 'Derzeit sind keine verwandten Titel verfügbar.',
        ageVerification: {
            title: 'Nur für Erwachsene',
            message: 'Dieser Inhalt ist nur für Erwachsene. Bitte bestätigen Sie, dass Sie fortfahren möchten.',
            minAgeHint: 'Sie müssen mindestens {age} Jahre alt sein, um diesen Titel anzusehen.',
            confirm: 'Ich bin volljährig, weiter',
            cancel: 'Abbrechen',
            missingDobTitle: 'Geburtsdatum erforderlich',
            missingDobMessage: 'Tragen Sie in Ihrem Profil ein Geburtsdatum ein, um diesen Inhalt anzusehen.',
            underageTitle: 'Altersbeschränkt',
            underageMessage: 'Sie müssen mindestens {age} Jahre alt sein, um diesen Inhalt anzusehen.',
        },
    },
    shareModal: {
        title: 'Teilen',
        copyLink: 'Link kopieren',
        copyLinkWithMessage: 'Link mit Nachricht kopieren',
        copyLinkWithMessageHint: 'Titel und Beschreibung enthalten',
        shareVia: 'Teilen über...',
        shareViaHint: 'Apps, Nachrichten, E-Mail',
        linkCopied: 'Link kopiert',
        linkCopiedMessage: 'Link in die Zwischenablage kopiert!',
        shared: 'Geteilt',
        sharedMessage: 'Inhalt erfolgreich geteilt'
    },
    contextMenu: {
        playFromBeginning: 'Von Anfang Abspielen',
        markAsFinished: 'Als Fertig Markieren',
        addToFavorites: 'Zu Favoriten Hinzufügen',
        removeFromFavorites: 'Aus Favoriten Entfernen',
        share: 'Teilen',
        viewDetails: 'Details Anzeigen',
        removeFromLibrary: 'Aus Bibliothek Entfernen',
        continuePlaying: 'Weiter Abspielen',
        continueReading: 'Weiterlesen'
    },
    actions: {
        subscribe: 'Abonnieren',
        signInToGetPremium: 'Premium holen',
        purchase: 'Kaufen',
        buy: 'Kaufen',
        buyFor: 'Kaufen für {price}',
        purchasedGoToLibrary: 'Gekauft - Zur Bibliothek',
        addToFavorites: 'Zu Favoriten hinzufügen',
        removeFromFavorites: 'Aus Favoriten entfernen',
        share: 'Teilen'
    },
    browse: {
        sortBy: 'Sortieren nach',
        sortOptions: {
            recent: 'Neueste',
            ascending: 'A-Z',
            descending: 'Z-A'
        }
    },
    cookieConsent: {
        title: 'Cookies',
        message: 'Wir verwenden Cookies, um Ihr Browsing-Erlebnis zu verbessern und unseren Traffic zu analysieren. Durch Klicken auf "Akzeptieren" stimmen Sie unserer Verwendung von Cookies zu. Weitere Informationen finden Sie in unserer',
        privacyPolicy: 'Datenschutzrichtlinie',
        accept: 'Akzeptieren',
        reject: 'Ablehnen'
    },

    legal: {
        companyNumber: 'Firmennummer',
        privacyPolicy: 'Datenschutzrichtlinie',
        termsOfService: 'Nutzungsbedingungen',
        agreeToTermsSubscribe: 'Mit dem Abonnement stimmen Sie unseren',
        operatedBy: '{appName} wird betrieben von {companyName}, {address}',
        faqs: 'FAQs'
    },
    support: {
        title: 'Support',
        description: 'Benötigen Sie Hilfe mit Ihrem Konto, Abonnement oder Ihrer Hörerfahrung? Durchsuchen Sie unsere FAQs und rechtlichen Informationen oder senden Sie uns eine Nachricht.',
        intro: 'Benötigen Sie Hilfe mit Ihrem Konto, Abonnement oder Ihrer Hörerfahrung? Durchsuchen Sie unsere FAQs und rechtlichen Informationen unten, oder kontaktieren Sie uns, wenn Sie weitere Unterstützung benötigen.',
        quickLinks: 'Schnelllinks',
        contactTitle: 'Support kontaktieren',
        contactDescription: 'Senden Sie eine Nachricht an unser Support-Team. Prüfen Sie zuerst die FAQs für schnelle Antworten.',
        contactIntro: 'Wenn Sie Hilfe benötigen, sind wir für Sie da. Viele häufige Fragen sind bereits in unserem Hilfezentrum beantwortet.',
        contactIntroFaqBefore: 'Bitte sehen Sie sich unsere ',
        contactIntroFaqAfter: ' für Antworten auf häufige Fragen an. Wenn Sie weiterhin Hilfe benötigen, füllen Sie das Formular aus und wir melden uns so schnell wie möglich.',
        contactSubmit: 'Nachricht senden',
        contactName: 'Name',
        contactEmail: 'E-Mail',
        contactSubject: 'Betreff',
        contactMessage: 'Nachricht',
        contactMessageHint: 'Bitte beschreiben Sie Ihr Anliegen ausführlich',
        backToSupport: 'Zurück zum Support',
        pageLoading: 'Wird geladen…',
        pageNotAvailable: 'Diese Seite ist derzeit nicht verfügbar.',
        unsupportedSection: 'Nicht unterstützter Inhaltsabschnitt: {name}',
        useContactFormBefore: 'Möchten Sie uns kontaktieren? Nutzen Sie unser ',
        contactSuccessTitle: 'Nachricht gesendet',
        contactSuccessMessage: 'Ihre Nachricht wurde gesendet. Wir melden uns so schnell wie möglich.',
        contactErrorTitle: 'Senden fehlgeschlagen',
        contactErrorMessage: 'Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
    },
    promo: {
        haveCode: 'Haben Sie einen Aktionscode?',
        placeholder: 'Code eingeben',
        apply: 'Anwenden',
        remove: 'Entfernen',
        applied: '{name} – {discount}',
        invalidCode: 'Ungültiger oder abgelaufener Code.',
        expired: 'Dieser Code ist abgelaufen.',
        notValidForPurchase: 'Dieser Code ist für diesen Einkauf nicht gültig.',
        free: 'Kostenlos',
        percentOff: '{value} % Rabatt',
        fixedOff: '{value} Rabatt',
        useWhenSubscribe: 'Nutzen Sie dieses Angebot bei Ihrer Anmeldung unten.',
        useWhenPurchase: 'Nutzen Sie diesen Code beim Kauf eines Titels.',
        accessGranted: 'Der Zugang wurde Ihrem Konto hinzugefügt.'
    }
}

export default messages

